import React, { useCallback, useEffect, useRef, useState } from 'react'
import Page from 'src/components/Page'
import { Box, Button, Tooltip, Typography } from '@material-ui/core'
import { alpha, useTheme } from '@material-ui/core/styles'
import AddIcon from '@material-ui/icons/Add'
import { useQueryClient } from 'react-query'
import AnalysisTable from './AnalysisTable'
import { GlobalScrollbarStyles } from './AnnotationView/styles/StyledComponents'
import { ANALYSIS_STATS_KEY } from './useAnalysisStats'
import { BATCHES_QUERY_KEY } from '../Batches/useBatches'
import NewAnalysisDialog from '../NewAnalysis/NewAnalysisDialog'
import { ReadyCardView } from './home/ReadyCard'
import SuggestedCard from './home/SuggestedCard'
import InProgressCard from './home/InProgressCard'
import useReadySamples from './home/useReadySamples'
import revealInHistory from './home/revealInHistory'
import { rowSampleLabel } from './home/model'
import { THREE_UP } from './home/layout'

// N opens New analysis, unless the user is typing or another dialog is open.
const isTypingTarget = (el) =>
  Boolean(el) && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName))

// Home: a header with the one solid action, then three cards (Ready to analyse:
// recent uploads with no analysis yet, and a drop target; Suggested: setups the
// user runs again and again, ready to open with the waiting samples they fit; In
// progress: unfinished batches and running analyses), then Analysis History.
// Every analysis starts in the New analysis panel; the older wizard lives on
// inside it as "Other analysis types".
const AnalysisView = function () {
  const theme = useTheme()
  const queryClient = useQueryClient()
  const historyRef = useRef(null)
  // The panel, and what it opens with: samples (from Ready to analyse or a
  // suggestion), and a suggestion's workflow and batch name.
  const [panel, setPanel] = useState({ open: false, samples: null, workflow: null, name: null })
  const [lastStart, setLastStart] = useState(0)
  const openPanel = useCallback(
    (samples = null, { workflow = null, name = null } = {}) => setPanel({ open: true, samples, workflow, name }),
    [],
  )
  const closePanel = useCallback(() => setPanel((prev) => ({ ...prev, open: false })), [])
  // Read once for Ready to analyse and Suggested, so a sample hidden in one leaves both.
  const ready = useReadySamples(lastStart)

  // A suggestion opens the panel preset; nothing starts until the user starts it there.
  const openSuggestion = useCallback(
    (suggestion) =>
      openPanel(suggestion.samples, { workflow: suggestion.workflow, name: suggestion.batchName || null }),
    [openPanel],
  )
  const openSetup = useCallback((suggestion) => openPanel(null, { workflow: suggestion.workflow }), [openPanel])

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.defaultPrevented || event.repeat || event.metaKey || event.ctrlKey || event.altKey) return
      if (event.key !== 'n' && event.key !== 'N') return
      if (isTypingTarget(event.target) || isTypingTarget(document.activeElement)) return
      if (document.querySelector('.MuiDialog-root, [role="dialog"]')) return
      event.preventDefault()
      openPanel()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [openPanel])

  // The panel confirms the start itself; Home only makes sure the history, the
  // navbar statistics, the batches, the file list and "Last used" catch up.
  const handleStarted = useCallback(() => {
    setLastStart(Date.now())
    queryClient.invalidateQueries('all-analysis')
    queryClient.invalidateQueries(ANALYSIS_STATS_KEY)
    queryClient.invalidateQueries(BATCHES_QUERY_KEY)
    queryClient.invalidateQueries('all-files')
  }, [queryClient])

  const showInHistory = useCallback(
    (row) =>
      revealInHistory(
        historyRef.current,
        rowSampleLabel(row),
        alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.14 : 0.22),
      ),
    [theme],
  )
  const scrollToHistory = useCallback(() => revealInHistory(historyRef.current, null), [])

  return (
    <Page title="Analyses | Genesus">
      <GlobalScrollbarStyles>
        <Box
          sx={{
            mt: 2,
            minHeight: 56,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="h4" component="h1">
            Analyses
          </Typography>
          <Tooltip title="New analysis (N)" arrow>
            <Button variant="contained" disableElevation startIcon={<AddIcon />} onClick={() => openPanel()}>
              New analysis
            </Button>
          </Tooltip>
        </Box>

        {/* Three cards, as the Files page sets its cards above its table: one row
            of equal cards from 1280px; from md, Ready to analyse and Suggested side
            by side with In progress below at full width; stacked on phones. */}
        <Box
          sx={{
            mt: 1,
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
            alignItems: 'stretch',
            gap: 3,
            '& > [data-home-card="progress"]': { gridColumn: { md: '1 / -1' } },
            [THREE_UP]: {
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              '& > [data-home-card="progress"]': { gridColumn: 'auto' },
            },
          }}
        >
          <ReadyCardView ready={ready} onOpen={openPanel} />
          <SuggestedCard ready={ready} onReview={openSuggestion} onUseSetup={openSetup} />
          <Box data-home-card="progress" sx={{ display: 'flex', minWidth: 0, '& > *': { flex: 1 } }}>
            <InProgressCard onReveal={showInHistory} onShowHistory={scrollToHistory} />
          </Box>
        </Box>

        {/* Analysis History. Its width does not follow the table's content, so on a
            narrow screen the table scrolls inside its card instead of widening the page.
            The scroll margin keeps it clear of the top bar when scrolled to. */}
        <Box ref={historyRef} sx={{ mt: 3, contain: 'inline-size', scrollMarginTop: 88 }}>
          <AnalysisTable />
        </Box>

        {/* TODO(suggestions): initialWorkflow and initialName are new props the panel
            does not read yet; the New analysis owner wires them in (see
            home/suggestions and SuggestedCard). Until then a suggestion opens the
            panel with its samples only. */}
        <NewAnalysisDialog
          open={panel.open}
          initialSamples={panel.samples}
          initialWorkflow={panel.workflow}
          initialName={panel.name}
          onClose={closePanel}
          onStarted={handleStarted}
        />
      </GlobalScrollbarStyles>
    </Page>
  )
}

export default AnalysisView
